import { Box, PaperProps, TextField, debounce } from '@mui/material'
import { CloseModalIcon } from 'images'
import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react'
import Draggable from 'react-draggable'
import { SHeader, SPaperLarge as SPaper, SDialog } from './styled'
import { AutoSuggest, Button } from 'core'
import { EmployeeAutoSuggestOption } from 'parts/EmployeeAndSkillAutoSuggestOption/EmployeeAndSkillAutoSuggestOption'
import { searchEmployees } from 'search/api/requests'
import { EmployeeInfo } from 'search/context/types'

interface DelegateModalProps {
    commentText: string
    setCommentText: React.Dispatch<React.SetStateAction<string>>
    onClose: () => void
    isOpen: boolean
    handleDelegate: (comment: string, userId: string) => Promise<void>
}

const DelegateModal: FC<DelegateModalProps> = memo(
    ({ commentText, setCommentText, onClose, isOpen, handleDelegate }) => {
        const nodeRef = React.useRef(null)

        const PaperComponent = useCallback(
            (props: PaperProps) => {
                return (
                    <Draggable
                        handle="#draggable-dialog-title"
                        cancel={'[class*="MuiDialogContent-root"]'}
                        nodeRef={nodeRef}
                    >
                        <SPaper {...props} ref={nodeRef} />
                    </Draggable>
                )
            },
            [nodeRef]
        )

        const [inputKey, setInputKey] = useState<number>(0)
        const [searchedEmployeesList, setSearchedEmployeesList] = useState<EmployeeInfo[]>([])
        const [employee, setEmployee] = useState<EmployeeInfo | null>(null)

        const handleInputChange = (input: string) => {
            filterEmployeesByInput(input)
        }

        const filterEmployeesByInput = debounce(async (input: string) => {
            const result = await searchEmployees(input)
            setSearchedEmployeesList(result || [])
        }, 500)

        const hasMounted = useRef(false)

        useEffect(() => {
            hasMounted.current = true

            return () => {
                hasMounted.current = false
            }
        }, [])

        useEffect(() => {
            ;(async () => {
                try {
                    const result = await searchEmployees()

                    if (!hasMounted.current) return

                    setSearchedEmployeesList(result || [])
                } catch (error) {
                    console.log(error)
                }
            })()

            return () => {
                setCommentText('')
                setSearchedEmployeesList([])
                setEmployee(null)
            }
        }, [isOpen])

        return (
            <SDialog
                ref={nodeRef}
                open={isOpen}
                onClose={onClose}
                hideBackdrop
                fullWidth={true}
                maxWidth={'xs'}
                aria-labelledby="draggable-dialog-title"
                PaperComponent={PaperComponent}
            >
                <SHeader id="draggable-dialog-title">
                    Delegate approve level
                    <Box component="div" sx={{ cursor: 'pointer' }}>
                        <CloseModalIcon
                            onClick={(event) => {
                                event.stopPropagation()
                                onClose()
                            }}
                        />
                    </Box>
                </SHeader>

                <Box component="div" sx={{ width: '300px' }}>
                    <AutoSuggest
                        id="delegate-modal-search-input"
                        key={inputKey}
                        placeholder="Select an employee"
                        onInputChange={handleInputChange}
                        disableClearable={true}
                        renderOption={EmployeeAutoSuggestOption}
                        options={searchedEmployeesList}
                        onChange={(value) => {
                            setEmployee(value)
                            setInputKey((prev) => prev + 1)
                        }}
                        value={employee}
                        color="white"
                    />
                </Box>

                <TextField
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder={'Comment for the selected employee'}
                    multiline
                    rows={2}
                    sx={{
                        '& .MuiOutlinedInput-input': { padding: '0px !important' },
                    }}
                    margin="none"
                />

                <Button
                    variant="contained"
                    onClick={() => handleDelegate(commentText, employee?.user_id || '')}
                >
                    Delegate
                </Button>
            </SDialog>
        )
    }
)

export default DelegateModal
